import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'

import classes from '../SettingsElements.module.scss'

import { Alert, Badge, Button, ButtonText, Feedback, Loader, Text, useToast } from '@percent/lemonade'
import { ReactComponent as GoogleCalendarIcon } from '@percent/workplace-giving/assets/icons/google-calendar.svg'
import { ReactComponent as OutlookCalendarIcon } from '@percent/workplace-giving/assets/icons/outlook-calendar.svg'
import { useLogger } from '@percent/workplace-giving/common/hooks/useLogger/useLogger'
import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { okResponse } from '@percent/workplace-giving/api/goodstack'
import { useAuth, useCalendarIntegrationStatus } from '@percent/workplace-giving/common/hooks'
import { getPartnerFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { disconnectCalendar } from '@percent/workplace-giving/api/calendar/disconnectCalendar'
import { OUTLOOK_OPPORTUNITY_ID_KEY } from '@percent/workplace-giving/utils/calendarOpportunity'
import {
  CalendarProvider,
  ConnectionStatus,
  useIntegrateWithCalendar
} from '@percent/workplace-giving/common/hooks/useIntegrateWithCalendar/useIntegrateWithCalendar'
import {
  getAvailableProviders,
  isProviderConnected
} from '@percent/workplace-giving/common/hooks/useIntegrateWithCalendar/calendarProvider.utils'
import { GoogleCalendarPreIntegrationModal } from '@percent/workplace-giving/common/components/GoogleCalendarPreIntegrationModal/GoogleCalendarPreIntegrationModal'

export function CalendarIntegration() {
  const { t } = useTranslation()
  const { logError } = useLogger()
  const { addToast } = useToast()
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)

  const [outlookOpportunityId] = useState<string | null>(() => {
    const params = new URLSearchParams(globalThis.location.search)
    const oppId = params.get('outlookOpportunityId')

    if (oppId) {
      sessionStorage.setItem(OUTLOOK_OPPORTUNITY_ID_KEY, oppId)
      params.delete('outlookOpportunityId')
      const remainingParams = params.toString()
      const cleanUrl = remainingParams
        ? `${globalThis.location.pathname}?${remainingParams}`
        : globalThis.location.pathname
      globalThis.history.replaceState({}, '', cleanUrl)
    }

    return oppId || sessionStorage.getItem(OUTLOOK_OPPORTUNITY_ID_KEY)
  })
  const hasAutoTriggered = useRef(false)

  const availableProviders = useMemo(
    () => getAvailableProviders(partner?.calendarIntegration),
    [partner?.calendarIntegration]
  )
  const [selectedProvider, setSelectedProvider] = useState<CalendarProvider | null>(null)
  const [preIntegrationModalOpen, setPreIntegrationModalOpen] = useState(false)
  const [loadingProviders, setLoadingProviders] = useState<Record<CalendarProvider, boolean>>({
    [CalendarProvider.GOOGLE]: false,
    [CalendarProvider.OUTLOOK]: false
  })
  const [disconnectingProvider, setDisconnectingProvider] = useState<CalendarProvider | null>(null)
  const [connectionStatuses, setConnectionStatuses] = useState<Record<CalendarProvider, ConnectionStatus>>({
    [CalendarProvider.GOOGLE]: ConnectionStatus.NOT_CONNECTED,
    [CalendarProvider.OUTLOOK]: ConnectionStatus.NOT_CONNECTED
  })

  const { data, isLoading: isLoadingStatus, error: statusError } = useCalendarIntegrationStatus()

  useEffect(() => {
    if (statusError) {
      logError(statusError)
    }
  }, [logError, statusError])

  useEffect(() => {
    if (!isLoadingStatus && data && !statusError) {
      setConnectionStatuses(prev => {
        const newStatuses = { ...prev }

        availableProviders.forEach(provider => {
          newStatuses[provider] =
            data.integrated && isProviderConnected(data.providers, provider)
              ? ConnectionStatus.CONNECTED
              : ConnectionStatus.NOT_CONNECTED
        })

        return newStatuses
      })
    }
  }, [data, isLoadingStatus, statusError, availableProviders])

  const { mutateAsync: disconnectCalendarMutation } = useMutation({
    mutationFn: disconnectCalendar
  })

  const googleConnectCalendar = useIntegrateWithCalendar({
    provider: CalendarProvider.GOOGLE,
    setLoadingState: (loading: boolean) => {
      setLoadingProviders(prev => ({ ...prev, [CalendarProvider.GOOGLE]: loading }))
    },
    setConnectionStatus: (status: ConnectionStatus) => {
      setConnectionStatuses(prev => ({
        ...prev,
        [CalendarProvider.GOOGLE]: status
      }))
    }
  })

  const outlookConnectCalendar = useIntegrateWithCalendar({
    provider: CalendarProvider.OUTLOOK,
    setLoadingState: (loading: boolean) => {
      setLoadingProviders(prev => ({ ...prev, [CalendarProvider.OUTLOOK]: loading }))
    },
    setConnectionStatus: (status: ConnectionStatus) => {
      setConnectionStatuses(prev => ({
        ...prev,
        [CalendarProvider.OUTLOOK]: status
      }))
    }
  })

  const getConnectCalendar = (provider: CalendarProvider) => {
    return provider === CalendarProvider.GOOGLE ? googleConnectCalendar : outlookConnectCalendar
  }

  const handleConnect = (provider: CalendarProvider) => {
    if (provider === CalendarProvider.OUTLOOK) {
      setLoadingProviders(prev => ({ ...prev, [CalendarProvider.OUTLOOK]: true }))
      getConnectCalendar(CalendarProvider.OUTLOOK)()

      return
    }

    setSelectedProvider(provider)
    setPreIntegrationModalOpen(true)
  }

  const handleDisconnect = async (provider: CalendarProvider) => {
    setDisconnectingProvider(provider)
    try {
      const res = await disconnectCalendarMutation([provider])

      if (okResponse(res)) {
        setConnectionStatuses(prev => ({
          ...prev,
          [provider]: ConnectionStatus.NOT_CONNECTED
        }))
      } else {
        logError(res)
        addToast(t('workplace_giving.errors.failedToDisconnectCalendar'), 'error')
      }
    } catch (error) {
      logError(error)
      addToast(t('workplace_giving.errors.failedToDisconnectCalendar'), 'error')
    } finally {
      setDisconnectingProvider(null)
    }
  }

  useEffect(() => {
    if (!outlookOpportunityId || isLoadingStatus || hasAutoTriggered.current) return

    const isOutlookConnected = data?.integrated && isProviderConnected(data.providers, CalendarProvider.OUTLOOK)

    if (isOutlookConnected) {
      hasAutoTriggered.current = true
      sessionStorage.removeItem(OUTLOOK_OPPORTUNITY_ID_KEY)
    } else if (data && !isOutlookConnected) {
      hasAutoTriggered.current = true
      setLoadingProviders(prev => ({ ...prev, [CalendarProvider.OUTLOOK]: true }))
      outlookConnectCalendar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlookOpportunityId, isLoadingStatus, data])

  return (
    <div className={classes.SettingsElementCard}>
      <div>
        <Text className={classes.SettingsElementTitle} fontWeight="semiBold" size="large" as="p">
          {t('workplace_giving.settings.calendarIntegration.title')}
        </Text>
        <Text className={classes.SettingsElementDescription} as="p" size="small">
          {t('workplace_giving.settings.calendarIntegration.description')}
        </Text>
      </div>

      {isLoadingStatus ? (
        <div className={classes.Loader}>
          <Loader isFullScreen={false} />
        </div>
      ) : null}

      {availableProviders.map(provider => {
        const status = connectionStatuses[provider]

        return (
          <div key={provider}>
            {status === ConnectionStatus.MISSING_PERMISSIONS && (
              <Alert
                variant="warning"
                title={t('workplace_giving.settings.calendarIntegration.insufficientPermissionsAlert.title')}
                className={classes.InfoAlert}
              >
                {t('workplace_giving.settings.calendarIntegration.insufficientPermissionsAlert.description')}
              </Alert>
            )}

            {!isLoadingStatus && status === ConnectionStatus.CONNECTED ? (
              <>
                <div className={classes.SettingsElementInfoWrapper}>
                  <Text size="small" fontWeight="medium">
                    {provider === CalendarProvider.GOOGLE ? 'Google Calendar' : 'Outlook Calendar'}
                  </Text>
                  <Badge variant="positive" icon="approve" shape="oval">
                    {t('workplace_giving.settings.connected')}
                  </Badge>
                  <ButtonText onClick={() => handleDisconnect(provider)} disabled={disconnectingProvider === provider}>
                    {t('workplace_giving.settings.disconnect')}
                  </ButtonText>
                </div>

                <Text className={classes.SettingsElementDescription} as="p" size="small">
                  <Trans
                    t={t}
                    i18nKey="workplace_giving.settings.calendarIntegration.connectedDescriptionMultipleProviders"
                    values={{
                      calendarName: provider === CalendarProvider.GOOGLE ? 'Google' : 'Outlook'
                    }}
                  />
                </Text>
              </>
            ) : null}

            {!isLoadingStatus && status !== ConnectionStatus.CONNECTED ? (
              <div className={classes.SettingsElementOption}>
                {provider === CalendarProvider.GOOGLE && (
                  <GoogleCalendarIcon className={classes.SettingsElementOptionIcon} />
                )}
                {provider === CalendarProvider.OUTLOOK && (
                  <OutlookCalendarIcon className={classes.SettingsElementOptionIcon} />
                )}
                <Text fontWeight="medium">
                  {provider === CalendarProvider.GOOGLE ? 'Google Calendar' : 'Outlook Calendar'}
                </Text>

                <Button
                  size="small"
                  variant="secondary"
                  shape="oval"
                  loading={loadingProviders[provider]}
                  loadingText={t('workplace_giving.settings.connecting')}
                  onClick={() => handleConnect(provider)}
                >
                  {status === ConnectionStatus.ERROR || status === ConnectionStatus.MISSING_PERMISSIONS
                    ? t('workplace_giving.common.retry')
                    : t('workplace_giving.settings.connect')}
                </Button>
              </div>
            ) : null}

            {!isLoadingStatus && status === ConnectionStatus.ERROR && (
              <div className={classes.SettingsElementFeedbackWrapper}>
                <Feedback variant="critical">
                  <Trans
                    t={t}
                    i18nKey="workplace_giving.settings.calendarIntegration.errorMultipleProviders"
                    values={{
                      calendarName: provider === CalendarProvider.GOOGLE ? 'Google' : 'Outlook'
                    }}
                  />
                </Feedback>
              </div>
            )}
          </div>
        )
      })}

      {selectedProvider === CalendarProvider.GOOGLE && (
        <GoogleCalendarPreIntegrationModal
          openModal={preIntegrationModalOpen}
          handleClose={() => {
            if (selectedProvider) {
              setLoadingProviders(prev => ({ ...prev, [selectedProvider]: false }))
            }
            setPreIntegrationModalOpen(false)
            setSelectedProvider(null)
          }}
          handleSubmit={() => {
            if (selectedProvider) {
              setLoadingProviders(prev => ({ ...prev, [selectedProvider]: true }))
            }
            setPreIntegrationModalOpen(false)
            const connectCalendar = getConnectCalendar(selectedProvider)
            connectCalendar()
          }}
        />
      )}
    </div>
  )
}
